import type { CreateEnquirySessionPayload } from 'api/accommodationBooking'
import { getHotelPricing } from 'lib/accommodation/buildEnquirySessionPayload'

function mapSupplierMetaData(
  rate: App.RawPropertyRate,
): CreateEnquirySessionPayload['supplierMetaData'] {
  if (rate.service.type === 'bedbank') {
    return {
      internalService: 'bedbank',
      selectedRate: {
        optionId: rate.optionId,
        internalRateId: rate.service.rateId,
        ...(rate.service.rateGroupId ? { rateGroupId: rate.service.rateGroupId } : {}),
        ...(rate.service.bedGroups[0]?.id ? { bedGroupId: rate.service.bedGroups[0].id } : {}),
      },
      internalPropertyId: rate.service.propertyId,
      internalRoomId: rate.service.roomId,
    }
  }

  return {
    internalService: 'reservation',
    selectedRate: {
      optionId: rate.optionId,
      internalRateId: rate.service.rateId,
      ...(rate.service.ratePlanId ? { ratePlanId: rate.service.ratePlanId } : {}),
      ...(rate.groupKey ? { rateGroupId: rate.groupKey } : {}),
    },
    internalPropertyId: rate.service.propertyId,
    internalRoomId: rate.service.roomId,
  }
}

export function getAccommodationV2RateIdentityFromRawRate(rate: App.RawPropertyRate): Pick<
  App.Checkout.AccommodationV2Item,
  'internalService' | 'internalRateId' | 'rateGroupId' | 'bedGroupId' | 'optionId'
> {
  if (rate.service.type === 'bedbank') {
    return {
      optionId: rate.optionId,
      internalService: 'bedbank',
      internalRateId: rate.service.rateId,
      rateGroupId: rate.service.rateGroupId,
      bedGroupId: rate.service.bedGroups[0]?.id,
    }
  }

  return {
    optionId: rate.optionId,
    internalService: 'reservation',
    internalRateId: rate.service.rateId,
    rateGroupId: rate.groupKey,
    bedGroupId: undefined,
  }
}

export function applyAccommodationV2ReplacementSession(
  item: App.Checkout.AccommodationV2Item,
  rate: App.RawPropertyRate,
  sessionId: string,
): App.Checkout.AccommodationV2Item {
  return {
    ...item,
    sessionId,
    ...getAccommodationV2RateIdentityFromRawRate(rate),
  }
}

/**
 * Rebuilds a session-create payload after a `price_changed` enquiry.
 * Pricing and rate identity come from the live rate; occupancy, dates, property,
 * room, and bundles stay on the existing session. LuxPlus+ eligibility comes from the
 * caller, as for `buildEnquirySessionPayloadFromCartItem`.
 *
 * The price token also comes from the live rate, and is omitted when that rate has none.
 * Never fall back to the existing session's token: the price change invalidated it.
 */
export function buildReplacementEnquirySessionPayload(
  session: App.AccommodationEnquirySession,
  rate: App.RawPropertyRate,
  isLuxPlusUser: boolean,
): CreateEnquirySessionPayload {
  const { occupancy, property, room, checkIn, checkOut, region, bundle, optInBundles } = session.data

  return {
    pricing: getHotelPricing(rate),
    property: { propertyId: property.propertyId },
    room: { roomId: room.roomId },
    occupancy,
    supplierMetaData: mapSupplierMetaData(rate),
    checkIn,
    checkOut,
    region,
    currency: rate.currency,
    ...(bundle ? { bundle } : {}),
    ...(optInBundles?.length ? { optInBundles } : {}),
    ...(isLuxPlusUser && {
      createdContext: { userContext: { isLuxPlusUser: true } },
    }),
  }
}
